/* eslint-disable no-mixed-spaces-and-tabs */
import { useCallback, useEffect, useRef, useState } from "react";
import Proximiio from "proximiio-js-library";
import { State } from "proximiio-js-library/lib/components/map/main";
import maplibregl, { LngLatLike } from "maplibre-gl";
import { useShallow } from "zustand/react/shallow";
import {
  osName,
  browserName,
  mobileModel,
  mobileVendor,
  deviceType,
} from "react-device-detect";
import useRouting from "@/hooks/useRouting";
import { FilterItemModel } from "@/models/filterItem.model";
import i18n from "@/i18n";

import useMapStore from "@/store/mapStore";
import {
  buildAdditionalFilters,
  getHiddenItems,
  kiosks as predefinedKiosks,
} from "@/store/data";
import Feature from "proximiio-js-library/lib/models/feature";
import { cn, compareValues, getPolygonCentroid } from "@/lib/utils";
import useFeaturesStore from "@/store/featuresStore";
import { FeatureCollection } from "geojson";
import { useIdle } from "react-use";
import { t } from "i18next";
import { KioskModel } from "@/models/kiosk.model";
import MapFailedAlert from "./MapFailedAlert";

function MapView() {
  const mapInitiated = useRef(false);
  const [findRoute] = useRouting();
  const defaultPlaceId = import.meta.env.VITE_WAYFINDING_DEFAULT_PLACE_ID;
  const refetchData =
    import.meta.env.VITE_WAYFINDING_AUTO_DATA_REFETCH === "true";
  const autoRouting =
    import.meta.env.VITE_WAYFINDING_KIOSK_AUTO_ROUTING === "true";

  // store state
  const kioskMode = useMapStore((state) => state.kioskMode);
  const gpsMode = useMapStore((state) => state.gpsMode);
  const currentLang = useMapStore((state) => state.currentLang);
  const filterCategories = useMapStore((state) => state.filterCategories);
  const map = useMapStore((state) => state.map);
  const routeFinish = useMapStore((state) => state.routeFinish);
  const routeStart = useMapStore((state) => state.routeStart);
  const activeFilter = useMapStore((state) => state.activeFilter);
  const activeKiosk = useMapStore((state) => state.activeKiosk);
  const accessibleRoute = useMapStore((state) => state.accessibleRoute);
  const forceAccessibleRoute = useMapStore(
    (state) => state.forceAccessibleRoute,
  );
  const appSession = useMapStore((state) => state.appSession);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const startFeatureStore = useMapStore((state) => state.startFeature);
  const haveRouteDetails = useMapStore((state) => state.haveRouteDetails);
  const features = useFeaturesStore((state) => state.features);
  const amenities = useMapStore((state) => state.amenities);

  // store actions
  const setMap = useMapStore(useShallow((state) => state.setMap));
  const setMapLoaded = useMapStore(useShallow((state) => state.setMapLoaded));
  const setPlaces = useMapStore((state) => state.setPlaces);
  const setCurrentPlace = useMapStore((state) => state.setCurrentPlace);
  const setFloors = useMapStore((state) => state.setFloors);
  const setKiosks = useMapStore((state) => state.setKiosks);
  const setCurrentFloor = useMapStore((state) => state.setCurrentFloor);
  const setFeatures = useFeaturesStore((state) => state.setFeatures);
  const setAmenities = useMapStore((state) => state.setAmenities);
  const setRouteStart = useMapStore((state) => state.setRouteStart);
  const setRouteFinish = useMapStore((state) => state.setRouteFinish);
  const setHaveRouteDetails = useMapStore((state) => state.setHaveRouteDetails);
  const setHaveRoutePreview = useMapStore((state) => state.setHaveRoutePreview);
  const setRouteDetails = useMapStore((state) => state.setRouteDetails);
  const setCurrentStep = useMapStore((state) => state.setCurrentStep);
  const setActiveFilter = useMapStore((state) => state.setActiveFilter);
  const setActiveKiosk = useMapStore((state) => state.setActiveKiosk);
  const setAccessibleRoute = useMapStore((state) => state.setAccessibleRoute);
  const setShowCustomRoutePicker = useMapStore(
    (state) => state.setShowCustomRoutePicker,
  );
  const setIsLoading = useMapStore((state) => state.setIsLoading);
  const locateMe = useMapStore((state) => state.locateMe);
  const setKioskMode = useMapStore((state) => state.setKioskMode);
  const setStepAnimated = useMapStore((state) => state.setStepAnimated);
  const centerToFinish = useMapStore((state) => state.centerToFinish);

  const prevRouteFinish = useRef<Feature>();
  const animationFrame = useRef<number>();
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const [alreadyRouted, setAlreadyRouted] = useState<boolean>(false);
  const [status, setStatus] = useState("");
  const poiMapClick = useRef(false);

  const mapPadding = {
    top: mobileMode ? 50 : 130,
    bottom: mobileMode ? 200 : 130,
    left: mobileMode ? 50 : currentLang === "ar" ? 250 : 650,
    right: mobileMode ? 50 : currentLang === "ar" ? 650 : 250,
  };

  const zoom = mobileMode ? 17 : import.meta.env.VITE_WAYFINDING_DEFAULT_ZOOM;
  const pitch = mobileMode ? 26 : import.meta.env.VITE_WAYFINDING_DEFAULT_PITCH;
  const bearing = mobileMode
    ? -87
    : import.meta.env.VITE_WAYFINDING_DEFAULT_BEARING;
  const defaultLocation = {
    coordinates: mobileMode
      ? [31.017063856307324, 29.9721325]
      : [
          +import.meta.env.VITE_WAYFINDING_DEFAULT_LOCATION_LONGITUDE,
          +import.meta.env.VITE_WAYFINDING_DEFAULT_LOCATION_LATITUDE,
        ],
    level: +import.meta.env.VITE_WAYFINDING_DEFAULT_LOCATION_LEVEL,
  };

  const idleTime = haveRouteDetails ? 60e3 : 40e3;
  const appIsIdle = useIdle(idleTime); // 60/40 seconds to idle time

  // finish select handler function
  const finishSelectHandler = useCallback(() => {
    console.log("routeFinish", routeFinish);
    // center the map, set the floor level to poi
    map.setFloorByLevel(routeFinish.properties.level);
    map.enablePolygonPreventedIcons();
    if (
      routeFinish.properties.hideIcon !== "hide" ||
      routeFinish.properties.metadata?.bays ||
      routeFinish.properties?.metadata?.prevent_polygon === true ||
      routeFinish.properties?.metadata?.prevent_polygon === "true"
    ) {
      map.setFeatureFilter(routeFinish.id);
      map.setFeaturesHighlight(
        [routeFinish.id],
        "#000000",
        58,
        0.5,
        true,
        0.5,
        [0, 2],
      );
      map.disablePolygonPreventedIcons();
      map.disablePolygons();
    } else {
      map.setFeaturesHighlight([]);
      setActiveFilter({} as FilterItemModel);
    }

    setShowCustomRoutePicker(false);
    if (mobileMode && !poiMapClick.current) {
      if (routeFinish.properties?._dynamic?.polygon_id) {
        map.getMapboxInstance().jumpTo({
          center: getPolygonCentroid({
            polygonId: routeFinish.properties._dynamic.polygon_id as string,
            features: map.getMapState().allFeatures.features,
          }) as LngLatLike,
          zoom: 16.5,
          padding: {
            top: 0,
            right: 0,
            left: 0,
            bottom: 250,
          },
        });
      } else {
        map.getMapboxInstance().jumpTo({
          center: routeFinish.geometry.coordinates as LngLatLike,
          zoom: 16.5,
          padding: {
            top: 0,
            right: 0,
            left: 0,
            bottom: 250,
          },
        });
      }
    }
    setTimeout(() => {
      setIsLoading(false);
      poiMapClick.current = false;
    }, 1000);
  }, [
    map,
    routeFinish,
    setActiveFilter,
    setShowCustomRoutePicker,
    setIsLoading,
    mobileMode,
  ]);

  const routeCancelHandler = useCallback(() => {
    console.log("routeFinish cancelled");
    if (Object.keys(map).length > 0) {
      // cancel route if it's rendered
      map.cancelRoute();
      // remove polygon selection after route finish is cancelled
      map.handlePolygonSelection();
      map.setFeaturesHighlight([]);
      map.enablePolygonPreventedIcons();
      if (!forceAccessibleRoute) {
        setAccessibleRoute({ accessibleRoute: false });
      }
      if (alreadyRouted && kioskMode && mobileMode) {
        map.stopKiosk();
        setKioskMode(false);
        const params = new URLSearchParams(location.search);
        params.delete("kiosk");
        params.delete("qrCode");
        history.replaceState(null, "", "?" + params + location.hash);
      }
    }
  }, [
    map,
    kioskMode,
    mobileMode,
    forceAccessibleRoute,
    alreadyRouted,
    setAccessibleRoute,
    setKioskMode,
  ]);

  const polygonPrevented = useCallback((feature: Feature) => {
    return (
      feature.properties?.metadata?.prevent_polygon === true ||
      feature.properties?.metadata?.prevent_polygon === "true"
    );
  }, []);

  // This effect hook handles route finish state changes
  useEffect(() => {
    if (routeFinish?.id) {
      if (prevRouteFinish?.current?.id !== routeFinish?.id) {
        // set timeout to postpone some stuff and show poi info first
        setTimeout(() => {
          finishSelectHandler();
        }, 500);
      }
    } else {
      if (prevRouteFinish?.current?.id) {
        // set timeout to postpone some stuff and show poi info first
        setTimeout(() => {
          routeCancelHandler();
        }, 500);
      }
    }
  }, [
    routeFinish,
    map,
    finishSelectHandler,
    routeCancelHandler,
    currentLang,
    mobileMode,
  ]);

  // This effect hook handles route start state changes
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    console.log("start efffect", routeStart);
    if (kioskMode && activeKiosk && routeFinish?.id) {
      findRoute({
        finish: routeFinish.id,
        preview: true,
      });
    }
    if (routeStart === "kiosk" || routeStart?.id) {
      console.log("routeStart", routeStart);
      // if we also have route finish generate route and reset the current step
      if (routeFinish?.id) {
        findRoute({
          finish: routeFinish.id,
          start: routeStart === "kiosk" ? undefined : routeStart.id,
        });
        setCurrentStep(0);
        setTimeout(() => {
          setStepAnimated(0);
        }, 2000);
        if (urlParams.get("followRoute") === "true" || mobileMode) {
          map.getMapboxInstance().jumpTo({
            center:
              routeStart === "kiosk"
                ? ([
                    activeKiosk?.longitude,
                    activeKiosk?.latitude,
                  ] as LngLatLike)
                : (routeStart.geometry.coordinates as LngLatLike),
            zoom: 18.5,
          });
        }
        map.handlePolygonSelection(
          routeStart !== "kiosk" && routeStart.properties?._dynamic?.polygon_id
            ? [
                polygonPrevented(routeFinish) ? undefined : routeFinish,
                polygonPrevented(routeStart) ? undefined : routeStart,
              ]
            : polygonPrevented(routeFinish)
              ? [routeStart as Feature, undefined]
              : [routeStart as Feature, routeFinish],
        );
        setAlreadyRouted(true);
        return;
      }
    }
    if (startFeatureStore.id) {
      findRoute({
        finish: routeFinish.id,
        start: startFeatureStore.id,
        preview: true,
      });
      map.handlePolygonSelection(
        routeStart !== "kiosk" && routeStart.properties?._dynamic?.polygon_id
          ? [
              polygonPrevented(routeFinish) ? undefined : routeFinish,
              polygonPrevented(routeStart) ? undefined : routeStart,
            ]
          : polygonPrevented(routeFinish)
            ? undefined
            : routeFinish,
      );
      return;
    }
  }, [
    routeStart,
    routeFinish,
    map,
    activeKiosk,
    kioskMode,
    findRoute,
    setCurrentStep,
    setStepAnimated,
    startFeatureStore,
    mobileMode,
    polygonPrevented,
  ]);

  // This effect hook handles active filter state changes
  useEffect(() => {
    console.log("active filter effect");
    setIsLoading(true);

    // set timeout to postpone some stuff and show poi info first
    setTimeout(() => {
      if (activeFilter?.id) {
        const foundCategory = filterCategories.find((category) =>
          category.items.find((items) => items.id === activeFilter.id),
        );
        console.log("active filter", activeFilter);
        // map.resetAmenityFilters();
        // workaround for keeping hidden all pois within the category hidden
        const hiddenCategory = filterCategories.find((i) => i.id === "hidden")!;
        map.setAmenityFilter(
          compareValues(hiddenCategory.items[0]!.id!, activeFilter!.id!)
            ? activeFilter!.id!
            : "nonexisting",
          "hidden",
        );

        map.setAmenityFilter(activeFilter!.id!, foundCategory!.title);
        // workaround to hide all poi icons by setting feature filter to polygon feature
        if (foundCategory?.id !== "facilities") {
          // find polygon prevented features under active amenity filter
          const polygonPrevented = features
            .filter((f) => {
              const activeFilterId = Array.isArray(activeFilter.id)
                ? activeFilter.id
                : [activeFilter.id];
              return (
                (f.properties?.metadata?.prevent_polygon === true ||
                  f.properties?.metadata?.prevent_polygon === "true") &&
                activeFilterId.includes(f.properties.amenity)
              );
            })
            .map((f) => f.id);
          if (polygonPrevented.length > 0) {
            for (const id of polygonPrevented) {
              // set them as active feature filter to keep their icons visible
              map.setFeatureFilter(id);
            }
          } else {
            // if there are no polygon prevented features set the active filter to polygon feature to hide all icons
            map.setFeatureFilter(
              "a59cce03-e052-4692-bf1d-64b0e9899422:a6b4a80e-7c58-4837-a978-9fca81b4c1ab",
            );
          }
        }
        setIsLoading(false);
      } else {
        console.log("active filter cancelled", activeFilter, routeFinish);
        if (Object.keys(map).length > 0) {
          map.resetAmenityFilters();
          map.resetFeatureFilters();

          // workaround for hiding all pois within the category hidden
          map.setAmenityFilter("nonexisting", "hidden");

          // handle polygon selection, only required when polygons are enabled
          if (routeFinish?.id) {
            setTimeout(() => {
              if (
                routeFinish.properties?.metadata?.prevent_polygon === true ||
                routeFinish.properties?.metadata?.prevent_polygon === "true"
              ) {
                map.setFeatureFilter(routeFinish.id);
              } else {
                map.handlePolygonSelection(
                  polygonPrevented(routeFinish) ? undefined : routeFinish,
                );
                map.setFeatureFilter(routeFinish.id);
              }
            });
          } else {
            map.enablePolygonPreventedIcons();
          }
          setTimeout(() => {
            setIsLoading(false);
          }, 1000);
        }
      }
    }, 500);
  }, [
    map,
    activeFilter,
    filterCategories,
    routeFinish,
    setIsLoading,
    polygonPrevented,
    features,
  ]);

  // This effect hook language change
  useEffect(() => {
    console.log("change language", currentLang);
    i18n.changeLanguage(currentLang);
    if (Object.keys(map).length > 0) {
      map.setLanguage(currentLang);
      map.setBoundsPadding({
        top: mobileMode ? 50 : 130,
        bottom: mobileMode ? 200 : 130,
        left: mobileMode ? 50 : currentLang === "ar" ? 250 : 650,
        right: mobileMode ? 50 : currentLang === "ar" ? 650 : 250,
      });
    }
    const rootHtml = document.getElementById("root-html");
    if (rootHtml) {
      rootHtml.setAttribute("dir", currentLang === "ar" ? "rtl" : "ltr");
      rootHtml.setAttribute("data-dir", currentLang === "ar" ? "rtl" : "ltr");
    }
  }, [currentLang, map, mobileMode]);

  useEffect(() => {
    prevRouteFinish.current = routeFinish;
  }, [routeFinish]);

  const rotateCamera = useCallback(
    (timestamp: number) => {
      // clamp the rotation between 0 -360 degrees
      // Divide timestamp by 100 to slow rotation to ~10 degrees / sec
      if (Object.keys(map).length > 0) {
        map
          .getMapboxInstance()
          .rotateTo((timestamp / 300) % 360, { duration: 0 });
      }
      // Request the next frame of the animation.
      animationFrame.current = requestAnimationFrame(rotateCamera);
    },
    [map],
  );

  // This effect hook set reset app session on idle time
  useEffect(() => {
    if (!mobileMode) {
      if (appIsIdle) {
        if (Object.keys(map).length > 0) {
          map.getMapboxInstance().flyTo({
            center: defaultLocation.coordinates as [number, number],
            pitch: 40,
            zoom: 17,
            duration: 2000,
          });
          map.getMapboxInstance().touchZoomRotate.disable();
          map.getMapboxInstance().touchPitch.disable();
          map.getMapboxInstance().dragPan.disable();
          map.getMapboxInstance().dragRotate.disable();
          timeout.current = setTimeout(() => {
            rotateCamera(0);
          }, 2300);
        }
      } else {
        clearTimeout(timeout.current);
        cancelAnimationFrame(animationFrame.current!);
        if (Object.keys(map).length > 0) {
          setTimeout(() => {
            locateMe();
          }, 100);
          setTimeout(() => {
            map.getMapboxInstance().touchZoomRotate.enable();
            map.getMapboxInstance().touchPitch.enable();
            map.getMapboxInstance().dragPan.enable();
            map.getMapboxInstance().dragRotate.enable();
          }, 1000);
        }
      }
    }
  }, [appIsIdle, bearing, map, mobileMode, rotateCamera, locateMe]);

  useEffect(() => {
    // Function to set the height of body and #proximiioMap
    const setHeight = () => {
      document.body.style.height = `${window.innerHeight}px`;
      const proximiioMap = document.getElementById("proximiioMap");
      if (proximiioMap) {
        proximiioMap.style.height = `${window.innerHeight}px`;
      }
    };

    // Set the height on initial load
    setHeight();

    // Add event listener for window resize to update heights
    window.addEventListener("resize", setHeight);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", setHeight);
    };
  }, []);

  useEffect(() => {
    const handleMessage = (
      event: MessageEvent<{ type: string; id: string }>,
    ) => {
      // Check the message's origin for security purposes
      // if (event.origin !== "https://trusted-origin.com") return;

      const { type, id } = event.data;

      const eventFeature = id
        ? features.find(
            (f) =>
              f.id?.toLowerCase() === id?.toLowerCase() ||
              f.properties.id?.toLowerCase() === id?.toLowerCase() ||
              f.properties.title?.toLowerCase() === id?.toLowerCase() ||
              f.properties.remote_id?.toString().toLowerCase() ===
                id?.toLowerCase(),
          )
        : undefined;

      switch (type) {
        case "SET_DESTINATION":
          console.log("set destination", id, eventFeature);
          if (eventFeature) {
            setRouteFinish(eventFeature);
            centerToFinish();
          }
          break;
        case "SET_START":
          console.log("set start", id, eventFeature);
          if (eventFeature) {
            setRouteStart(eventFeature);
          }
          break;
        default:
          console.warn("Unknown message type:", type);
      }
    };

    // Add the event listener to handle messages
    window.addEventListener("message", handleMessage);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [features, setRouteFinish, setRouteStart, centerToFinish]); // Empty dependency array ensures this effect runs once on mount

  useEffect(() => {
    if (Object.keys(map).length > 0) {
      buildAdditionalFilters({
        features,
        amenities,
        language: currentLang,
      });

      // set amenity category group from store/data.ts
      for (const category of filterCategories) {
        map.setAmenitiesCategory(
          category.title,
          category.items.map((i) => (i.id ? i.id : "undefined")).flat(2),
        );
      }

      map.setHiddenAmenities(getHiddenItems() as string[]);
    }
  }, [amenities, features, currentLang, map, filterCategories]);

  useEffect(() => {
    // Initialize map only once
    if (mapInitiated.current) return;
    mapInitiated.current = true;

    let routeLoading = false;
    let kiosk = {} as KioskModel;
    const urlParams = new URLSearchParams(window.location.search);

    const initMap = () => {
      setStatus("");
      // Authenticate Proximiio.js library and create map instance with provided constructor options
      Proximiio.Auth.setToken(import.meta.env.VITE_PROXIMIIO_TOKEN)
        .then(() => {
          setStatus("INIT_SUCCESS");
          // when authenticated create a map instance with provided constuctor options, check out library readme https://github.com/proximiio/proximiio-js-library#map-component
          const map = new Proximiio.Map({
            bundleUrl:
              import.meta.env.MODE !== "development"
                ? import.meta.env.VITE_PROXIMIIO_BUNDLE_URL
                : undefined,
            // you can define any of the mapbox options in there, we are retrieving those from state service
            mapboxOptions: {
              center: defaultLocation.coordinates as [number, number],
              zoom: zoom,
              pitch:
                kioskMode && activeKiosk?.pitch ? activeKiosk.pitch : pitch,
              bearing:
                kioskMode && activeKiosk?.bearing
                  ? activeKiosk.bearing
                  : bearing,
            },
            defaultPlaceId: defaultPlaceId, // if you have more than 1 place in your account, it's a good idea to define defaultPlaceId for the map, otherwise the first one will be picked up
            isKiosk: kioskMode, // if enabled starting point for routing will be based on values defined in kioskSettings, if disabled findRoute methods will expect start point to be send.
            kioskSettings: {
              coordinates:
                kioskMode && activeKiosk?.longitude && activeKiosk?.latitude
                  ? [activeKiosk.longitude, activeKiosk.latitude]
                  : (defaultLocation.coordinates as [number, number]),
              level:
                kioskMode && activeKiosk?.level
                  ? activeKiosk.level
                  : defaultLocation.level,
              showPoint: true,
              showLabel: true,
              pointColor: "183,0,0",
              labelFont: ["Futura", "GE-SS-Two", "sans-serif"],
            },
            fitBoundsPadding: mapPadding, // setting the padding option to use for zooming into the bounds when route is drawn,
            minFitBoundsDistance: 5,
            handleUrlParams: true, // enable handling url params, this way you can load map with predefined route generated
            urlParams: {
              destinationFeature: "remote_id",
            },
            language: currentLang, // init with predefined language setting
            useGpsLocation: gpsMode, // if enabled your location will be detected with geolocation API and used as a starting point for routing
            geolocationControlOptions: {
              autoTrigger: true, // if enabled map will automatically enable geolocation
              autoLocate: false, // if enabled map will automatically focus on user location
              position: "top-right", //  position on the map to which the control will be added.
            },
            showLevelDirectionIcon: true, // if enabled arrow icon will be shown at the levelchanger indicating direction of level change along the found route
            levelDirectionOutlineColor: "#b60000",
            levelDirectionPopupImage:
              "data:image/png;base64,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",
            initPolygons: true,
            polygonsOptions: {
              defaultPolygonColor: "#4F091D", // optional, default: '#dbd7e8', default color of the polygons
              hoverPolygonColor: "#4F091D", // optional, default: '#a58dfa', hover color of the polygons
              selectedPolygonColor: "#4F091D", // optional, default: '#6945ed', selected color of the polygons
              defaultLabelColor: "#ffffff", // optional, default: '#6945ed', default color of the polygon labels
              defaultPolygonHeight: 5, // optional, default: 3, default polygon height in meters
              hoverPolygonHeight: 6, // optional, default: 3, hover polygon height in meters
              selectedPolygonHeight: 6, // optional, default: 3, selected polygon height in meters
              removeOriginalPolygonsLayer: true,
              minZoom: 15,
              labelMinZoom: 18,
              iconMinZoom: 16,
              disabledPolygonColor: "#666",
              disabledLabelColor: "rgba(255,255,255, 1)",
              disabledPolygonHeight: 4,
              adaptiveLabelOpacity: true,
              adaptiveMaxPitch: 26,
              drawRouteUnderPolygons: true,
              textFont: ["Futura Light Light", "GE SS Two Light"],
              labelFontSize: [
                "interpolate",
                ["exponential", 1.75],
                ["zoom"],
                18, // Zoom level 18
                [
                  "interpolate",
                  ["linear"],
                  [
                    "/",
                    ["get", "length", ["get", "_dynamic"]],
                    ["/", ["length", ["get", "title"]], 1.5],
                  ],
                  1,
                  13, // Map smallest font size
                  10,
                  25, // Map medium font size
                  15,
                  35, // Map largest font size
                ],
                22, // Zoom level 22
                [
                  "interpolate",
                  ["linear"],
                  [
                    "/",
                    ["get", "length", ["get", "_dynamic"]],
                    ["/", ["length", ["get", "title"]], 1.5],
                  ],
                  1,
                  23, // Map smallest font size at higher zoom
                  10,
                  35, // Map medium font size at higher zoom
                  15,
                  45, // Map largest font size at higher zoom
                ],
              ],
            },
            polygonLayers: [
              {
                featureType: "shop",
              },
              {
                featureType: "shop-booth",
                defaultPolygonHeight: 2,
              },
              {
                featureType: "anchor-shop",
                iconImage: "{id}",
              },
              {
                featureType: "dining",
                defaultPolygonColor: "#0C6D70",
                hoverPolygonColor: "#0C6D70",
                selectedPolygonColor: "#0C6D70",
              },
              {
                featureType: "dining-booth",
                defaultPolygonHeight: 2,
                defaultPolygonColor: "#0C6D70",
                hoverPolygonColor: "#0C6D70",
                selectedPolygonColor: "#0C6D70",
              },
              {
                featureType: "entertainment",
                defaultPolygonColor: "#657d92",
                hoverPolygonColor: "#657d92",
                selectedPolygonColor: "#657d92",
              },
              {
                featureType: "anchor-entertainment",
                iconImage: "{id}",
                defaultPolygonColor: "#657d92",
                hoverPolygonColor: "#657d92",
                selectedPolygonColor: "#657d92",
              },
              {
                featureType: "stay",
                defaultPolygonColor: "#91814F",
                hoverPolygonColor: "#91814F",
                selectedPolygonColor: "#91814F",
              },
              {
                featureType: "facilities",
                defaultPolygonColor: "#0D1B33",
                hoverPolygonColor: "#0D1B33",
                selectedPolygonColor: "#0D1B33",
              },
              {
                featureType: "parking_bays0",
                defaultPolygonColor: "#383838",
                hoverPolygonColor: "#5867a1",
                selectedPolygonColor: "#5867a1",
                disabledPolygonColor: "#383838",
                defaultPolygonHeight: 0.5,
                hoverPolygonHeight: 0.5,
                selectedPolygonHeight: 0.5,
                disabledPolygonHeight: 0.5,
                defaultLabelColor: "rgba(0,0,0,0)",
                selectedLabelColor: "rgba(0,0,0,0)",
                hoverLabelColor: "rgba(0,0,0,0)",
                disabledLabelColor: "rgba(0,0,0,0)",
                opacity: 0.4,
              },
              {
                featureType: "parking_bays1",
                defaultPolygonColor: "#383838",
                hoverPolygonColor: "#588a5b",
                selectedPolygonColor: "#588a5b",
                disabledPolygonColor: "#383838",
                defaultPolygonHeight: 0.5,
                hoverPolygonHeight: 0.5,
                selectedPolygonHeight: 0.5,
                disabledPolygonHeight: 0.5,
                defaultLabelColor: "rgba(0,0,0,0)",
                selectedLabelColor: "rgba(0,0,0,0)",
                hoverLabelColor: "rgba(0,0,0,0)",
                disabledLabelColor: "rgba(0,0,0,0)",
                opacity: 0.4,
              },
              {
                featureType: "parking_bays2",
                defaultPolygonColor: "#383838",
                hoverPolygonColor: "#a76b62",
                selectedPolygonColor: "#a76b62",
                disabledPolygonColor: "#383838",
                defaultPolygonHeight: 0.5,
                hoverPolygonHeight: 0.5,
                selectedPolygonHeight: 0.5,
                disabledPolygonHeight: 0.5,
                defaultLabelColor: "rgba(0,0,0,0)",
                selectedLabelColor: "rgba(0,0,0,0)",
                hoverLabelColor: "rgba(0,0,0,0)",
                disabledLabelColor: "rgba(0,0,0,0)",
                opacity: 0.4,
              },
              {
                featureType: "parking_bays3",
                defaultPolygonColor: "#383838",
                hoverPolygonColor: "#bc5c5e",
                selectedPolygonColor: "#bc5c5e",
                disabledPolygonColor: "#383838",
                defaultPolygonHeight: 0.5,
                hoverPolygonHeight: 0.5,
                selectedPolygonHeight: 0.5,
                disabledPolygonHeight: 0.5,
                defaultLabelColor: "rgba(0,0,0,0)",
                selectedLabelColor: "rgba(0,0,0,0)",
                hoverLabelColor: "rgba(0,0,0,0)",
                disabledLabelColor: "rgba(0,0,0,0)",
                opacity: 0.4,
              },
              {
                featureType: "parking_bays4",
                defaultPolygonColor: "#383838",
                hoverPolygonColor: "#b3a658",
                selectedPolygonColor: "#b3a658",
                disabledPolygonColor: "#383838",
                defaultPolygonHeight: 0.5,
                hoverPolygonHeight: 0.5,
                selectedPolygonHeight: 0.5,
                disabledPolygonHeight: 0.5,
                defaultLabelColor: "rgba(0,0,0,0)",
                selectedLabelColor: "rgba(0,0,0,0)",
                hoverLabelColor: "rgba(0,0,0,0)",
                disabledLabelColor: "rgba(0,0,0,0)",
                opacity: 0.4,
              },
            ],
            routeAnimation: {
              enabled: true,
              type: "point",
              followRoute: true,
              followRouteAngle: false,
              looping: false,
              pointIconUrl:
                "https://proximi.ams3.digitaloceanspaces.com/walking-man-moe.png",
              pointIconAsMarker: true,
              pointIconMarkerSize: mobileMode ? 300 * 0.175 : 300 * 0.25,
              durationMultiplier: mobileMode ? 0.8 : 1.5,
              lineColor: "#b60000",
              lineWidth: 10,
              minzoom: 10,
            },
            routeColor: "#DBB1B1",
            blockFeatureClickWhileRouting: true,
            defaultFloorLevel: +import.meta.env
              .VITE_WAYFINDING_DEFAULT_LOCATION_LEVEL,
            zoomIntoPlace: false,
            hiddenAmenities: getHiddenItems() as string[],
            useRasterTiles: true,
            rasterTilesOptions: {
              useProxy: import.meta.env.MODE === "development",
              minZoom: 12,
              maxZoom: 20,
              bounds: [
                31.01265981524233, 29.968469994037733, 31.023962135266174,
                29.975626609046884,
              ],
            },
            /*featuresMaxBounds: [
            [55.437980892653286, 25.17300768646389],
            [55.32613455606534, 25.30698731225813],
          ],*/
            autoLevelChange: true,
            poiIconSize: [
              "interpolate",
              ["exponential", 0.5],
              ["zoom"],
              17,
              0.25,
              22,
              0.7,
            ],
            pmTilesUrl:
              import.meta.env.MODE !== "development"
                ? import.meta.env.VITE_PROXIMIIO_PMTILES_URL
                : undefined,
            /*localSources: {
						features:
							urlParams.get('useLocalSources') === 'true'
								? new FeatureCollection(featuresJSON)
								: new FeatureCollection([]),
						amenities:
							urlParams.get('useLocalSources') === 'true'
								? amenitiesJSON as AmenityModel[]
								: [] as AmenityModel[],
					},*/
          });

          map.getMapReadyListener().subscribe((ready) => {
            console.log("map ready", ready);
            const mapState: State = map.getMapState();

            // setting mapbox navigationControl buttons
            map.getMapboxInstance().addControl(
              new maplibregl.NavigationControl({
                showZoom: false,
              }),
            );

            map.getMapboxInstance().setMaxZoom(20);
            map.getMapboxInstance().on("touchstart", (e) => {
              if (e.originalEvent.touches.length > 2) {
                // e.preventDefault(); // Prevent default touch behavior
              }
            });

            setMap(map);
            const places = mapState.places;
            setPlaces(places);
            setCurrentPlace(mapState.place);
            setFloors(
              mapState.floors.filter((f) => f.placeId === mapState.place.id),
            );
            setCurrentFloor(mapState.floor);
            const features = mapState.allFeatures.features.filter(
              (feature) =>
                (feature.properties.usecase === "poi" ||
                  feature.properties.type === "poi") &&
                feature.geometry.type === "Point" &&
                feature.properties.type !== "escalator" &&
                feature.properties.type !== "elevator" &&
                feature.properties.type !== "staircase" &&
                feature.properties.place_id === defaultPlaceId &&
                feature.properties.title?.trim() !== "" &&
                feature.properties.available !== false,
            );
            setFeatures(features);
            setAmenities(mapState.amenities);
            const kiosks = [
              ...predefinedKiosks,
              ...mapState.kiosks.map((kiosk) => {
                return {
                  ...kiosk,
                  latitude: kiosk.coordinates.lat,
                  longitude: kiosk.coordinates.lng,
                };
              }),
            ];
            setKiosks(kiosks);

            // This function handles URL query parameters related to start, destination and place
            setTimeout(() => {
              const startParam = urlParams.get("startFeature"); // in case you change url param name in urlParams option of map constuctor, change that too
              const destinationParam = urlParams.get("remote_id"); // in case you change url param name in urlParams option of map constuctor, change that too
              const placeParam = urlParams.get("defaultPlace"); // in case you change url param name in urlParams option of map constuctor, change that too

              const startFeature = startParam
                ? features.find(
                    (f) =>
                      f.id?.toLowerCase() === startParam?.toLowerCase() ||
                      f.properties.id?.toLowerCase() ===
                        startParam?.toLowerCase() ||
                      f.properties.title?.toLowerCase() ===
                        startParam?.toLowerCase() ||
                      f.properties.remote_id?.toString().toLowerCase() ===
                        startParam?.toLowerCase(),
                  )
                : undefined;
              if (startFeature) {
                setRouteStart(startFeature);
              }

              console.log();

              const destinationFeature = destinationParam
                ? features.find(
                    (f) =>
                      f.id?.toLowerCase() === destinationParam?.toLowerCase() ||
                      f.properties.id?.toLowerCase() ===
                        destinationParam?.toLowerCase() ||
                      f.properties.title?.toLowerCase() ===
                        destinationParam?.toLowerCase() ||
                      f.properties.remote_id?.toString().toLowerCase() ===
                        destinationParam?.toLowerCase(),
                  )
                : undefined;
              if (destinationFeature) {
                setRouteFinish(destinationFeature);
              }

              const defaultPlace = placeParam
                ? places.find(
                    (p) => p.id === placeParam || p.name === placeParam,
                  )
                : undefined;
              if (defaultPlace) {
                map.setPlace(defaultPlace.id);
              }

              if (kiosks.length > 0) {
                // handle kiosk mode intitialization
                if (urlParams.get("kiosk")) {
                  kiosk = kiosks.find(
                    (kiosk) =>
                      kiosk.name?.toLowerCase() ===
                        urlParams.get("kiosk")?.toLowerCase() ||
                      kiosk.id?.toLowerCase() ===
                        urlParams.get("kiosk")?.toLowerCase(),
                  ) as KioskModel;

                  const kioskPoi = features.find((f) => f.id === kiosk?.poiId);

                  if (kiosk?.bounds) {
                    map.getMapboxInstance().setMaxBounds(kiosk.bounds);
                  }
                  if (kiosk?.zoom) {
                    map.getMapboxInstance().setZoom(kiosk.zoom);
                  }
                  if (kiosk?.bearing) {
                    map.getMapboxInstance().setBearing(kiosk.bearing);
                  }
                  if (kiosk?.pitch) {
                    map.getMapboxInstance().setPitch(kiosk.pitch);
                  }

                  if (kiosk) {
                    if (kioskPoi) {
                      kiosk.longitude = kioskPoi.geometry.coordinates[0];
                      kiosk.latitude = kioskPoi.geometry.coordinates[1];
                      kiosk.level = kioskPoi.properties.level;
                    }

                    if (
                      kiosk.longitude &&
                      kiosk.latitude &&
                      typeof kiosk.level !== "undefined"
                    ) {
                      map.setKiosk(
                        kiosk.latitude,
                        kiosk.longitude,
                        kiosk.level,
                      );
                      if (routeFinish?.id) {
                        findRoute({
                          finish: routeFinish.id,
                          preview: true,
                        });
                      }
                      if (autoRouting) {
                        setTimeout(() => {
                          setRouteStart("kiosk");
                        }, 1000);
                      }
                    }
                  }

                  setActiveKiosk(kiosk);
                }
              }

              // save feature select log for qr code scans
              const qrParam = urlParams.get("qrCode");
              if (qrParam) {
                let featureAmenityCategory = "";
                const featureSection = destinationFeature?.properties?.amenity
                  ? filterCategories.find((category) =>
                      category.items.find((item) => {
                        if (
                          (Array.isArray(item.id) &&
                            item.id.includes(
                              destinationFeature.properties.amenity,
                            )) ||
                          item.id === destinationFeature.properties.amenity
                        ) {
                          featureAmenityCategory = item.title;
                          return true;
                        }
                      }),
                    )
                  : undefined;

                console.log("LOG: save qr scan log");
                const userData = {
                  osName,
                  browserName,
                  mobileModel,
                  mobileVendor,
                  deviceType:
                    kioskMode && !mobileMode && kiosk?.name
                      ? kiosk?.name
                      : deviceType,
                };
                new Proximiio.InteractionLogger({
                  interactionType: "qr",
                  targetElementType: destinationFeature?.id
                    ? "feature"
                    : undefined,
                  targetElementTitle: destinationFeature?.properties?.title
                    ? destinationFeature.properties.title
                    : undefined,
                  targetElementId: destinationFeature?.id
                    ? destinationFeature.id
                    : undefined,
                  targetElementAmenity: featureAmenityCategory
                    ? t(featureAmenityCategory, { lng: "en" })
                    : undefined,
                  targetElementAmenityCategory: featureSection
                    ? t(featureSection.title, { lng: "en" })
                    : undefined,
                  kioskId: kiosk?.id ? kiosk.id : undefined,
                  kioskTitle: kiosk?.name ? kiosk.name : undefined,
                  metadata: userData,
                  language: currentLang,
                  session: appSession,
                });
              }
            });

            // start route animation and cancel loading state when route is fully loaded
            map.getMapboxInstance().on("sourcedata", (ev) => {
              if (
                ev.sourceId === "route" &&
                ev.isSourceLoaded &&
                (ev.source as { data: FeatureCollection }).data.features
                  .length > 0
              ) {
                if (routeLoading) {
                  setIsLoading(false);
                  routeLoading = false;
                  map.restartRouteAnimation({ delay: 500, recenter: false });
                }
              }
            });
          });

          map.getMainSourceLoadedListener().subscribe(() => {
            console.log("map fully loaded");
            setMapLoaded(true);
          });

          // set data refetching interval if enabled in .env file
          if (refetchData) {
            setInterval(
              () => {
                map.refetch();
              },
              +import.meta.env.VITE_WAYFINDING_REFETCH_INTERVAL,
            );
          }

          // set destination point for routing based on click event and cancel previous route if generated
          map.getPolygonClickListener().subscribe((feature) => {
            if (prevRouteFinish?.current?.id !== feature?.id) {
              setIsLoading(true);
              setRouteFinish(feature);
              poiMapClick.current = true;
            }

            //save feature click log
            console.log("LOG: save feature click log");
            let featureAmenityCategory = "";
            const featureSection = feature?.properties?.amenity
              ? filterCategories.find((category) =>
                  category.items.find((item) => {
                    if (
                      (Array.isArray(item.id) &&
                        item.id.includes(feature.properties.amenity)) ||
                      item.id === feature.properties.amenity
                    ) {
                      featureAmenityCategory = item.title;
                      return true;
                    }
                  }),
                )
              : undefined;

            const userData = {
              osName,
              browserName,
              mobileModel,
              mobileVendor,
              deviceType:
                kioskMode && !mobileMode && kiosk?.name
                  ? kiosk?.name
                  : deviceType,
            };

            new Proximiio.InteractionLogger({
              interactionType: "mapclick",
              targetElementType: "feature",
              targetElementId: feature ? feature.id : undefined,
              targetElementTitle: feature
                ? feature.properties.title
                : undefined,
              targetElementAmenity: t(featureAmenityCategory, { lng: "en" }),
              targetElementAmenityCategory: featureSection
                ? t(featureSection.title, { lng: "en" })
                : "",
              kioskId: kiosk?.id ? kiosk?.id : undefined,
              kioskTitle: kiosk?.name ? kiosk?.name : undefined,
              metadata: userData,
              language: currentLang,
              session: appSession,
            });
          });

          // subscribe to map place selection listener, this always run once at map initiation and upon map.setPlace method call
          map.getPlaceSelectListener().subscribe((place) => {
            setCurrentPlace(place);
          });

          // subscribe to map floor selection listener, this always run once at map initiation and upon map.setFloor method call
          map.getFloorSelectListener().subscribe((floor) => {
            setCurrentFloor(floor);
          });

          // subscribe to route found listener
          map.getRouteFoundListener().subscribe((res) => {
            console.log("route found", res);
            map.stopRouteAnimation();
            if (res.details && res.TBTNav) {
              if (res.preview) {
                setHaveRoutePreview(true);
                setHaveRouteDetails(false);
              } else {
                // stop route animation and set loading state, until route is fully loaded, will be handled in sourcedata listener
                setIsLoading(true);
                routeLoading = true;
                setHaveRoutePreview(false);
                setHaveRouteDetails(true);
              }
              setRouteDetails(res);
            }
          });

          // subscribe to route failed listener
          map.getRouteFailedListener().subscribe((res) => {
            console.log("route failed", res);
            // if route found failed, try again with non/accessible route
            setAccessibleRoute({ accessibleRoute: !accessibleRoute });
          });

          // subscribe to nav step listener and set current step from that
          map.getNavStepSetListener().subscribe((step) => {
            setCurrentStep(step);
            setTimeout(() => {
              setStepAnimated(step);
            }, 2000);
          });
        })
        .catch((err) => {
          console.error("LOGIN FAILED", err);
          setStatus("INIT_FAILED");
          setTimeout(initMap, 120e3);
        });
    };

    initMap();
  });

  return (
    <>
      {status === "INIT_FAILED" ? (
        <div className="z-[200] mx-auto flex items-center justify-center">
          <MapFailedAlert />
        </div>
      ) : (
        <>
          <div
            id="proximiioMap"
            className={cn(
              "absolute h-screen w-full overflow-hidden group-data-[dir=ltr]:ml-0 group-data-[dir=rtl]:mr-0",
            )}
          ></div>
        </>
      )}
    </>
  );
}
export default MapView;
